import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
} from "@mui/material";
import * as React from "react";

import styles from "./AlertDialog.module.css";

export interface AlertDialogProps {
  dialogOpen: boolean;
  handleClose: () => void;
  dialogTitle: string;
  dialogText?: string;
  dialogContent?: React.ReactNode;
  dialogAction?: () => Promise<void>;
  /** Leaves the dialog open once `dialogAction` settles, for a caller that closes it itself. */
  suppressAutoClose?: boolean;
  /**
   * Renders a single Close button that only dismisses, for a dialog that informs and asks nothing.
   * `dialogAction`, `suppressAutoClose`, both labels and `destructive` are then ignored.
   */
  acknowledgeOnly?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Renders the confirm as the dangerous choice — filled and red against an outlined cancel —
   * so the two options do not read alike. Both are plain text buttons otherwise.
   */
  destructive?: boolean;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({
  dialogOpen,
  handleClose,
  dialogTitle,
  dialogText,
  dialogContent,
  dialogAction = () => Promise.resolve(),
  suppressAutoClose,
  acknowledgeOnly,
  confirmLabel = "Yes",
  cancelLabel = "No",
  destructive,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState<boolean>(false);

  const actionButtons = acknowledgeOnly ? (
    <Button onClick={handleClose} autoFocus>
      Close
    </Button>
  ) : (
    // A fragment, not a wrapper element: DialogActions spaces only its direct children.
    <>
      <Button
        variant={destructive ? "outlined" : undefined}
        onClick={handleClose}
      >
        {cancelLabel}
      </Button>

      <Button
        variant={destructive ? "contained" : undefined}
        color={destructive ? "error" : undefined}
        data-cy="confirm-action"
        onClick={() => {
          setLoading(true);
          void dialogAction().finally(() => {
            if (!suppressAutoClose) {
              setLoading(false);
              handleClose();
            }
          });
        }}
        autoFocus
      >
        {confirmLabel}
      </Button>
    </>
  );

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      // This absolutely needs to be here so that the dialog can be used in the viz workspace
      // because of https://github.com/roboto-ai/roboto-hosted-app/blob/main/web-ui/src/shared/components/visualization/DndContainer/PointerSensor.ts#L7
      data-not-draggable="true"
      maxWidth="md"
      sx={{ zIndex: theme.zLayer(10) }}
    >
      <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
      <DialogContent>
        {dialogText && (
          <DialogContentText id="alert-dialog-description">
            {dialogText}
          </DialogContentText>
        )}
        {dialogContent}
      </DialogContent>
      {loading ? (
        <DialogActions className={`${styles.actions} ${styles.loadingRow}`}>
          <CircularProgress size={"1.5rem"} />
        </DialogActions>
      ) : (
        <DialogActions className={styles.actions}>
          {actionButtons}
        </DialogActions>
      )}
    </Dialog>
  );
};
